import { z } from 'zod';
import {
  insertProfileSchema,
  insertTechnicianScheduleSchema,
  insertRobotSchema,
  insertClientSchema,
  insertProspectSchema,
  insertVehicleSchema,
  insertProductSchema,
  insertResellerSchema,
  insertMissionSchema,
  missionRobotClientAssignmentSchema,
  insertTaskSchema,
  insertTicketSchema,
  insertTicketNoteSchema,
  insertTaskCommentSchema,
  insertRobotClientSchema,
  insertSavDossierSchema,
  insertSavInterventionSchema,
  insertSavPieceSchema,
  profiles,
  technicianSchedules,
  robots,
  clients,
  prospects,
  vehicles,
  products,
  resellers,
  missions,
  tasks,
  taskComments,
  tickets,
  ticketNotes,
  ticketMissions,
  robotsClients,
  savDossiers,
  savInterventions,
  savPieces,
} from './schema';

export const errorSchemas = {
  validation: z.object({
    message: z.string(),
    field: z.string().optional(),
  }),
  notFound: z.object({
    message: z.string(),
  }),
  internal: z.object({
    message: z.string(),
  }),
  unauthorized: z.object({
    message: z.string(),
  })
};

// API routes structure
export const api = {
  auth: {
    me: {
      method: 'GET' as const,
      path: '/api/auth/me' as const,
      responses: {
        200: z.custom<typeof profiles.$inferSelect>(),
        401: errorSchemas.unauthorized,
      }
    }
  },
  profiles: {
    list: {
      method: 'GET' as const,
      path: '/api/profiles' as const,
      responses: {
        200: z.array(z.custom<typeof profiles.$inferSelect>()),
      }
    },
    get: {
      method: 'GET' as const,
      path: '/api/profiles/:id' as const,
      responses: {
        200: z.custom<typeof profiles.$inferSelect>(),
        404: errorSchemas.notFound,
      }
    },
    update: {
      method: 'PUT' as const,
      path: '/api/profiles/:id' as const,
      input: insertProfileSchema.partial(),
      responses: {
        200: z.custom<typeof profiles.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      }
    }
  },
  technicianSchedules: {
    list: {
      method: 'GET' as const,
      path: '/api/schedules' as const,
      responses: {
        200: z.array(z.custom<typeof technicianSchedules.$inferSelect>()),
      }
    },
    create: {
      method: 'POST' as const,
      path: '/api/schedules' as const,
      input: insertTechnicianScheduleSchema,
      responses: {
        201: z.custom<typeof technicianSchedules.$inferSelect>(),
        400: errorSchemas.validation,
      }
    },
    update: {
      method: 'PUT' as const,
      path: '/api/schedules/:id' as const,
      input: insertTechnicianScheduleSchema.partial(),
      responses: {
        200: z.custom<typeof technicianSchedules.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      }
    }
  },
  robots: {
    list: {
      method: 'GET' as const,
      path: '/api/robots' as const,
      responses: {
        200: z.array(z.custom<typeof robots.$inferSelect>()),
      }
    },
    get: {
      method: 'GET' as const,
      path: '/api/robots/:id' as const,
      responses: {
        200: z.custom<typeof robots.$inferSelect>(),
        404: errorSchemas.notFound,
      }
    },
    create: {
      method: 'POST' as const,
      path: '/api/robots' as const,
      input: insertRobotSchema,
      responses: {
        201: z.custom<typeof robots.$inferSelect>(),
        400: errorSchemas.validation,
      }
    },
    update: {
      method: 'PUT' as const,
      path: '/api/robots/:id' as const,
      input: insertRobotSchema.partial(),
      responses: {
        200: z.custom<typeof robots.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      }
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/robots/:id' as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      }
    }
  },
  clients: {
    list: {
      method: 'GET' as const,
      path: '/api/clients' as const,
      responses: {
        200: z.array(z.custom<typeof clients.$inferSelect>()),
      }
    },
    get: {
      method: 'GET' as const,
      path: '/api/clients/:id' as const,
      responses: {
        200: z.custom<typeof clients.$inferSelect>(),
        404: errorSchemas.notFound,
      }
    },
    create: {
      method: 'POST' as const,
      path: '/api/clients' as const,
      input: insertClientSchema.omit({ clientType: true }),
      responses: {
        201: z.custom<typeof clients.$inferSelect>(),
        400: errorSchemas.validation,
      }
    },
    update: {
      method: 'PUT' as const,
      path: '/api/clients/:id' as const,
      input: insertClientSchema.omit({ clientType: true }).partial(),
      responses: {
        200: z.custom<typeof clients.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      }
    }
  },
  prospects: {
    list: {
      method: 'GET' as const,
      path: '/api/prospects' as const,
      responses: {
        200: z.array(z.custom<typeof prospects.$inferSelect>()),
      }
    },
    get: {
      method: 'GET' as const,
      path: '/api/prospects/:id' as const,
      responses: {
        200: z.custom<typeof prospects.$inferSelect>(),
        404: errorSchemas.notFound,
      }
    },
    create: {
      method: 'POST' as const,
      path: '/api/prospects' as const,
      input: insertProspectSchema,
      responses: {
        201: z.custom<typeof prospects.$inferSelect>(),
        400: errorSchemas.validation,
      }
    },
    update: {
      method: 'PUT' as const,
      path: '/api/prospects/:id' as const,
      input: insertProspectSchema.partial(),
      responses: {
        200: z.custom<typeof prospects.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      }
    },
    transform: {
      method: 'POST' as const,
      path: '/api/prospects/:id/transform' as const,
      input: z.object({ type: z.enum(["client", "revendeur"]) }),
      responses: {
        200: z.object({ prospectId: z.string(), clientId: z.string().optional(), resellerId: z.string().optional() }),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      }
    }
  },
  vehicles: {
    list: {
      method: 'GET' as const,
      path: '/api/vehicles' as const,
      responses: {
        200: z.array(z.custom<typeof vehicles.$inferSelect>()),
      }
    },
    create: {
      method: 'POST' as const,
      path: '/api/vehicles' as const,
      input: insertVehicleSchema,
      responses: {
        201: z.custom<typeof vehicles.$inferSelect>(),
        400: errorSchemas.validation,
      }
    },
    update: {
      method: 'PUT' as const,
      path: '/api/vehicles/:id' as const,
      input: insertVehicleSchema.partial(),
      responses: {
        200: z.custom<typeof vehicles.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      }
    }
  },
  products: {
    list: {
      method: 'GET' as const,
      path: '/api/products' as const,
      responses: {
        200: z.array(z.custom<typeof products.$inferSelect>()),
      }
    },
    create: {
      method: 'POST' as const,
      path: '/api/products' as const,
      input: insertProductSchema,
      responses: {
        201: z.custom<typeof products.$inferSelect>(),
        400: errorSchemas.validation,
      }
    },
    update: {
      method: 'PUT' as const,
      path: '/api/products/:id' as const,
      input: insertProductSchema.partial(),
      responses: {
        200: z.custom<typeof products.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      }
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/products/:id' as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      }
    }
  },
  resellers: {
    list: {
      method: 'GET' as const,
      path: '/api/resellers' as const,
      responses: {
        200: z.array(z.custom<typeof resellers.$inferSelect>()),
      }
    },
    get: {
      method: 'GET' as const,
      path: '/api/resellers/:id' as const,
      responses: {
        200: z.custom<typeof resellers.$inferSelect>(),
        404: errorSchemas.notFound,
      }
    },
    create: {
      method: 'POST' as const,
      path: '/api/resellers' as const,
      input: insertResellerSchema,
      responses: {
        201: z.custom<typeof resellers.$inferSelect>(),
        400: errorSchemas.validation,
      }
    },
    update: {
      method: 'PUT' as const,
      path: '/api/resellers/:id' as const,
      input: insertResellerSchema.partial(),
      responses: {
        200: z.custom<typeof resellers.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
        409: z.object({ message: z.string() }),
      }
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/resellers/:id' as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      }
    }
  },
  missions: {
    list: {
      method: 'GET' as const,
      path: '/api/missions' as const,
      responses: {
        200: z.array(z.custom<typeof missions.$inferSelect>()),
      }
    },
    get: {
      method: 'GET' as const,
      path: '/api/missions/:id' as const,
      responses: {
        200: z.custom<typeof missions.$inferSelect>(),
        404: errorSchemas.notFound,
      }
    },
    create: {
      method: 'POST' as const,
      path: '/api/missions' as const,
      input: z.object({
        ...insertMissionSchema.shape,
        startDate: z.coerce.date(),
        endDate: z.coerce.date(),
        technicianAssignments: z.array(z.object({
          technicianId: z.string(),
          isRemote: z.boolean().optional(),
        })).optional(),
        technicianIds: z.array(z.string()).optional(),
        robotIds: z.array(z.string()).optional(),
        vehicleIds: z.array(z.string()).optional(),
        commercialIds: z.array(z.string()).optional()
        ,
        clientRobotAssignments: z.array(missionRobotClientAssignmentSchema).optional()
      }),
      responses: {
        201: z.custom<typeof missions.$inferSelect>(),
        400: errorSchemas.validation,
      }
    },
    update: {
      method: 'PUT' as const,
      path: '/api/missions/:id' as const,
      input: z.object({
        ...insertMissionSchema.partial().shape,
        startDate: z.coerce.date().optional(),
        endDate: z.coerce.date().optional(),
        technicianAssignments: z.array(z.object({
          technicianId: z.string(),
          isRemote: z.boolean().optional(),
        })).optional(),
        technicianIds: z.array(z.string()).optional(),
        robotIds: z.array(z.string()).optional(),
        vehicleIds: z.array(z.string()).optional(),
        commercialIds: z.array(z.string()).optional()
        ,
        clientRobotAssignments: z.array(missionRobotClientAssignmentSchema).optional()
      }),
      responses: {
        200: z.custom<typeof missions.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      }
    },
    actionDeparture: {
      method: 'POST' as const,
      path: '/api/missions/:id/robots/:robotId/departure' as const,
      input: z.object({
        photos: z.array(z.string()).length(4)
      }),
      responses: {
        200: z.object({ success: z.boolean() }),
        400: errorSchemas.validation,
      }
    },
    actionReturn: {
      method: 'POST' as const,
      path: '/api/missions/:id/robots/:robotId/return' as const,
      input: z.object({
        photos: z.array(z.string()).length(4)
      }),
      responses: {
        200: z.object({ success: z.boolean() }),
        400: errorSchemas.validation,
      }
    }
  },
  tasks: {
    list: {
      method: 'GET' as const,
      path: '/api/tasks' as const,
      responses: {
        200: z.array(z.custom<typeof tasks.$inferSelect>()),
      }
    },
    get: {
      method: 'GET' as const,
      path: '/api/tasks/:id' as const,
      responses: {
        200: z.custom<typeof tasks.$inferSelect>(),
        404: errorSchemas.notFound,
      }
    },
    create: {
      method: 'POST' as const,
      path: '/api/tasks' as const,
      input: insertTaskSchema,
      responses: {
        201: z.custom<typeof tasks.$inferSelect>(),
        400: errorSchemas.validation,
      }
    },
    update: {
      method: 'PUT' as const,
      path: '/api/tasks/:id' as const,
      input: insertTaskSchema.partial(),
      responses: {
        200: z.custom<typeof tasks.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      }
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/tasks/:id' as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      }
    }
  },
  taskComments: {
    list: {
      method: 'GET' as const,
      path: '/api/tasks/:taskId/comments' as const,
      responses: {
        200: z.array(z.custom<typeof taskComments.$inferSelect>()),
      }
    },
    create: {
      method: 'POST' as const,
      path: '/api/tasks/:taskId/comments' as const,
      input: insertTaskCommentSchema.omit({ taskId: true }),
      responses: {
        201: z.custom<typeof taskComments.$inferSelect>(),
        400: errorSchemas.validation,
      }
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/task-comments/:id' as const,
      responses: {
        200: z.object({ message: z.string() }),
      }
    }
  },
  tickets: {
    list: {
      method: 'GET' as const,
      path: '/api/tickets' as const,
      responses: {
        200: z.array(z.custom<typeof tickets.$inferSelect>()),
      }
    },
    get: {
      method: 'GET' as const,
      path: '/api/tickets/:id' as const,
      responses: {
        200: z.custom<typeof tickets.$inferSelect>(),
        404: errorSchemas.notFound,
      }
    },
    create: {
      method: 'POST' as const,
      path: '/api/tickets' as const,
      input: insertTicketSchema,
      responses: {
        201: z.custom<typeof tickets.$inferSelect>(),
        400: errorSchemas.validation,
      }
    },
    update: {
      method: 'PUT' as const,
      path: '/api/tickets/:id' as const,
      input: insertTicketSchema.partial(),
      responses: {
        200: z.custom<typeof tickets.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      }
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/tickets/:id' as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      }
    },
    byClient: {
      method: 'GET' as const,
      path: '/api/clients/:clientId/tickets' as const,
      responses: {
        200: z.array(z.custom<typeof tickets.$inferSelect>()),
      }
    },
    byReseller: {
      method: 'GET' as const,
      path: '/api/resellers/:resellerId/tickets' as const,
      responses: {
        200: z.array(z.custom<typeof tickets.$inferSelect>()),
      }
    },
    notes: {
      list: {
        method: 'GET' as const,
        path: '/api/tickets/:id/notes' as const,
        responses: {
          200: z.array(z.custom<typeof ticketNotes.$inferSelect>()),
        }
      },
      create: {
        method: 'POST' as const,
        path: '/api/tickets/:id/notes' as const,
        input: z.object({ content: z.string().min(1) }),
        responses: {
          201: z.custom<typeof ticketNotes.$inferSelect>(),
          400: errorSchemas.validation,
        }
      }
    },
    missions: {
      list: {
        method: 'GET' as const,
        path: '/api/tickets/:id/missions' as const,
        responses: {
          200: z.array(z.custom<typeof ticketMissions.$inferSelect>()),
        }
      },
      create: {
        method: 'POST' as const,
        path: '/api/tickets/:id/missions' as const,
        input: z.object({ missionId: z.string().uuid() }),
        responses: {
          201: z.custom<typeof ticketMissions.$inferSelect>(),
          400: errorSchemas.validation,
        }
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/tickets/:ticketId/missions/:missionId' as const,
        responses: {
          200: z.object({ message: z.string() }),
        }
      }
    }
  },
  sav: {
    list: {
      method: 'GET' as const,
      path: '/api/sav' as const,
      responses: {
        200: z.array(z.custom<typeof savDossiers.$inferSelect>()),
      }
    },
    get: {
      method: 'GET' as const,
      path: '/api/sav/:id' as const,
      responses: {
        200: z.custom<typeof savDossiers.$inferSelect>(),
        404: errorSchemas.notFound,
      }
    },
    create: {
      method: 'POST' as const,
      path: '/api/sav' as const,
      input: insertSavDossierSchema,
      responses: {
        201: z.custom<typeof savDossiers.$inferSelect>(),
        400: errorSchemas.validation,
      }
    },
    update: {
      method: 'PUT' as const,
      path: '/api/sav/:id' as const,
      input: insertSavDossierSchema.partial(),
      responses: {
        200: z.custom<typeof savDossiers.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      }
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/sav/:id' as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      }
    },
    interventions: {
      list: {
        method: 'GET' as const,
        path: '/api/sav/:id/interventions' as const,
        responses: {
          200: z.array(z.custom<typeof savInterventions.$inferSelect>()),
        }
      },
      create: {
        method: 'POST' as const,
        path: '/api/sav/:id/interventions' as const,
        input: insertSavInterventionSchema.omit({ dossierId: true }),
        responses: {
          201: z.custom<typeof savInterventions.$inferSelect>(),
          400: errorSchemas.validation,
        }
      }
    },
    pieces: {
      list: {
        method: 'GET' as const,
        path: '/api/sav/:id/pieces' as const,
        responses: {
          200: z.array(z.custom<typeof savPieces.$inferSelect>()),
        }
      },
      create: {
        method: 'POST' as const,
        path: '/api/sav/:id/pieces' as const,
        input: insertSavPieceSchema.omit({ dossierId: true }),
        responses: {
          201: z.custom<typeof savPieces.$inferSelect>(),
          400: errorSchemas.validation,
        }
      },
      update: {
        method: 'PUT' as const,
        path: '/api/sav/pieces/:pieceId' as const,
        input: insertSavPieceSchema.partial(),
        responses: {
          200: z.custom<typeof savPieces.$inferSelect>(),
          400: errorSchemas.validation,
        }
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/sav/pieces/:pieceId' as const,
        responses: {
          200: z.object({ message: z.string() }),
        }
      }
    }
  },

  robotsClients: {
    list: {
      method: 'GET' as const,
      path: '/api/clients/:clientId/robots' as const,
      responses: {
        200: z.array(z.custom<typeof robotsClients.$inferSelect>()),
      }
    },
    get: {
      method: 'GET' as const,
      path: '/api/robots-clients/:id' as const,
      responses: {
        200: z.custom<typeof robotsClients.$inferSelect>(),
        404: errorSchemas.notFound,
      }
    },
    create: {
      method: 'POST' as const,
      path: '/api/clients/:clientId/robots' as const,
      input: insertRobotClientSchema.omit({ clientId: true }),
      responses: {
        201: z.custom<typeof robotsClients.$inferSelect>(),
        400: errorSchemas.validation,
      }
    },
    update: {
      method: 'PUT' as const,
      path: '/api/robots-clients/:id' as const,
      input: insertRobotClientSchema.partial(),
      responses: {
        200: z.custom<typeof robotsClients.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      }
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/robots-clients/:id' as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      }
    },
    savHistory: {
      method: 'GET' as const,
      path: '/api/robots-clients/:id/sav' as const,
      responses: {
        200: z.array(z.custom<typeof savDossiers.$inferSelect>()),
      }
    },
  },
};

export function buildUrl(path: string, params?: Record<string, string | number>): string {
  let url = path;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (url.includes(`:${key}`)) {
        url = url.replace(`:${key}`, String(value));
      }
    });
  }
  return url;
}
